import React, { useState, useEffect } from 'react';
import { 
  ShieldAlert, Users, Cpu, Activity, UserPlus, Ban, CheckCircle, 
  Trash2, RefreshCw, Key, ShieldCheck, Zap, Server, Clock, AlertTriangle, ArrowLeft 
} from 'lucide-react';
import { adminService } from '../../services/api';

export default function AdminPortal({ onBackToDashboard }) {
  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'ai-hub' | 'telemetry'
  const [users, setUsers] = useState([]);
  const [aiHubStatus, setAiHubStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);

  // New user form state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    role: 'USER_TRADER',
    licenseDays: 30,
    dailyTokenQuota: 100000,
    licenseTier: 'PRO'
  });

  // AI Hub edit state
  const [selectedProvider, setSelectedProvider] = useState('MOCK_SANDBOX');
  const [providerConfig, setProviderConfig] = useState({
    apiKey: '',
    endpoint: '',
    baseURL: '',
    model: ''
  });

  // Load users & AI Hub status
  const loadData = async () => {
    setIsLoading(true);
    try {
      const [usersRes, aiRes] = await Promise.all([
        adminService.getUsers().catch(() => ({ users: [] })),
        adminService.getAIHub().catch(() => null)
      ]);
      setUsers(usersRes?.users || []);
      if (aiRes) {
        setAiHubStatus(aiRes);
        setSelectedProvider(aiRes.activeProvider || 'MOCK_SANDBOX');
      }
    } catch (err) {
      console.error('Failed to load admin data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await adminService.createUser(newUser);
      setShowCreateModal(false);
      setNewUser({
        username: '',
        password: '',
        role: 'USER_TRADER',
        licenseDays: 30,
        dailyTokenQuota: 100000,
        licenseTier: 'PRO'
      });
      setActionMessage('Tạo tài khoản và cấp License thành công!');
      loadData();
    } catch (err) {
      alert(`Lỗi tạo tài khoản: ${err.message}`);
    }
  };

  const handleSuspendUser = async (userId) => {
    if (!confirm('Khóa tài khoản này ngay lập tức? Toàn bộ phiên làm việc của user sẽ bị ngắt trong < 1ms.')) return;
    try {
      await adminService.suspendUser(userId);
      setActionMessage('Đã khóa tài khoản và hủy toàn bộ phiên làm việc tức thì.');
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await adminService.activateUser(userId);
      setActionMessage('Đã kích hoạt lại tài khoản thành công.');
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleExtendLicense = async (userId) => {
    const days = prompt('Nhập số ngày muốn gia hạn:', '30');
    if (!days) return;
    try {
      await adminService.extendLicense(userId, Number(days));
      setActionMessage(`Đã gia hạn thành công thêm ${days} ngày.`);
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Xóa vĩnh viễn tài khoản này?')) return;
    try {
      await adminService.deleteUser(userId);
      setActionMessage('Đã xóa người dùng khỏi hệ thống.');
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSwitchAI = async (providerId) => {
    try {
      const res = await adminService.updateAIHub(providerId, providerConfig);
      setAiHubStatus(res.status);
      setSelectedProvider(res.activeProvider);
      setActionMessage(`Đã chuyển đổi nóng sang ${res.activeProvider} thành công mà không cần khởi động lại server!`);
    } catch (err) {
      alert(`Lỗi cấu hình AI Provider: ${err.message}`);
    }
  };

  return (
    <div className="w-full px-2 sm:px-4 lg:px-6 py-6 space-y-6 animate-fade-in font-sans">
      
      {/* 1. Header Toolbar */}
      <div className="glass-panel p-5 sm:p-6 rounded-2xl cyber-bracket border border-fuchsia-500/30 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 rounded-xl bg-fuchsia-500/10 border border-fuchsia-400/40 flex items-center justify-center text-fuchsia-400 shadow-[0_0_20px_rgba(217,70,239,0.3)] flex-shrink-0">
            <Key className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h2 className="text-xl sm:text-2xl font-display font-bold text-white tracking-wide uppercase">
                TITAN // <span className="text-fuchsia-400">ADMIN CONTROL CENTER</span>
              </h2>
              <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/50">
                ROOT PRIVILEGES
              </span>
            </div>
            <p className="text-xs text-slate-400 font-mono mt-1">
              Quản trị người dùng, cấp phát bản quyền, cấu hình AI Gateway và giám sát phòng thủ Anti-DDoS
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={onBackToDashboard}
            className="px-3.5 py-2 rounded-xl bg-slate-900 border border-white/10 hover:border-cyan-400 text-slate-300 hover:text-white transition-all text-xs font-mono font-semibold flex items-center space-x-1.5 cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>VỀ WORKSPACE</span>
          </button>

          <button
            type="button"
            onClick={loadData}
            disabled={isLoading}
            className="p-2 rounded-xl bg-slate-900 border border-white/10 hover:border-fuchsia-400 text-slate-300 hover:text-fuchsia-400 transition-all cursor-pointer"
            title="Làm mới dữ liệu"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin text-fuchsia-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* Action Notification Banner */}
      {actionMessage && (
        <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-mono flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span>{actionMessage}</span>
          </div>
          <button 
            type="button" 
            onClick={() => setActionMessage(null)}
            className="text-slate-400 hover:text-white underline cursor-pointer"
          >
            Đóng
          </button>
        </div>
      )}

      {/* 2. Navigation Tabs */}
      <div className="flex items-center space-x-2 border-b border-white/10 pb-2">
        <button
          type="button"
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-xl text-xs font-mono font-bold transition-all flex items-center space-x-2 cursor-pointer ${
            activeTab === 'users'
              ? 'bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/40 shadow-[0_0_15px_rgba(217,70,239,0.2)]'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>QUẢN LÝ NGƯỜI DÙNG & LICENSE ({users.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('ai-hub')}
          className={`px-4 py-2 rounded-xl text-xs font-mono font-bold transition-all flex items-center space-x-2 cursor-pointer ${
            activeTab === 'ai-hub'
              ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-[0_0_15px_rgba(0,240,255,0.2)]'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <Cpu className="w-4 h-4" />
          <span>AI GATEWAY HUB (HOT-SWAP)</span>
        </button>
      </div>

      {/* TAB 1: USER MANAGEMENT */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-xs font-mono text-slate-400 uppercase">
              Danh sách tài khoản & thời hạn bản quyền
            </span>

            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 rounded-xl bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-mono font-bold text-xs flex items-center space-x-1.5 transition-all shadow-[0_0_15px_rgba(217,70,239,0.4)] cursor-pointer"
            >
              <UserPlus className="w-4 h-4" />
              <span>TẠO TÀI KHOẢN MỚI</span>
            </button>
          </div>

          <div className="glass-card rounded-2xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left font-mono text-xs">
                <thead className="bg-slate-950/80 text-slate-400 border-b border-white/10 uppercase text-[10px]">
                  <tr>
                    <th className="p-4">Tài khoản / Vai trò</th>
                    <th className="p-4">License Key</th>
                    <th className="p-4">Hạn dùng</th>
                    <th className="p-4">Hạn mức Token</th>
                    <th className="p-4">Trạng thái</th>
                    <th className="p-4 text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {users.map((u) => {
                    const isSuspended = u.status === 'SUSPENDED';
                    const isAdmin = u.role === 'ADMIN_SUPERUSER';

                    return (
                      <tr key={u.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="p-4">
                          <div className="font-bold text-white text-sm">{u.username}</div>
                          <div className="text-[10px] text-slate-500">{u.role}</div>
                        </td>

                        <td className="p-4 font-mono text-cyan-300">
                          {u.license_key || 'SYSTEM-GENERATED'}
                        </td>

                        <td className="p-4">
                          <div className="text-slate-300">
                            {new Date(u.license_expires_at).toLocaleDateString()}
                          </div>
                          <div className="text-[10px] text-slate-500">
                            {new Date(u.license_expires_at) > new Date() ? 'Còn hạn' : 'Hết hạn'}
                          </div>
                        </td>

                        <td className="p-4 text-slate-300">
                          {u.daily_tokens_used || 0} / {u.daily_token_quota}
                        </td>

                        <td className="p-4">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                            isSuspended 
                              ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40' 
                              : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                          }`}>
                            {u.status}
                          </span>
                        </td>

                        <td className="p-4 text-right space-x-1">
                          {!isAdmin && (
                            <>
                              {isSuspended ? (
                                <button
                                  type="button"
                                  onClick={() => handleActivateUser(u.id)}
                                  className="px-2.5 py-1 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 text-[11px] cursor-pointer"
                                  title="Mở khóa tài khoản"
                                >
                                  Mở khóa
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleSuspendUser(u.id)}
                                  className="px-2.5 py-1 rounded bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 text-[11px] cursor-pointer"
                                  title="Khóa ngay lập tức (Sub-1ms kickout)"
                                >
                                  Khóa ngay
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() => handleExtendLicense(u.id)}
                                className="px-2.5 py-1 rounded bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 text-[11px] cursor-pointer"
                                title="Gia hạn thêm ngày"
                              >
                                +Gia hạn
                              </button>

                              <button
                                type="button"
                                onClick={() => handleDeleteUser(u.id)}
                                className="p-1 rounded text-slate-500 hover:text-rose-400 transition-colors cursor-pointer"
                                title="Xóa tài khoản"
                              >
                                <Trash2 className="w-3.5 h-3.5 inline" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: DYNAMIC AI HUB CONFIG */}
      {activeTab === 'ai-hub' && (
        <div className="space-y-6">
          <div className="glass-panel p-5 rounded-2xl border border-cyan-500/30">
            <h3 className="text-sm font-mono font-bold text-white uppercase tracking-wider mb-2 flex items-center gap-2">
              <Zap className="w-4 h-4 text-cyan-400" />
              <span>Chuyển đổi AI Provider Nóng (Zero-Restart Dynamic Hub)</span>
            </h3>
            <p className="text-xs font-mono text-slate-400">
              Chọn nguồn AI để hệ thống sử dụng. Mọi thay đổi áp dụng tức thì mà không làm gián đoạn người dùng.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
              {[
                { id: 'MOCK_SANDBOX', name: 'Mock Sandbox Engine', desc: '5 Kịch bản SMC thông minh, không cần API Key thật.' },
                { id: 'HYPER_GRAVITY', name: 'Hyper Gravity HFT', desc: 'Endpoint AI phân tích tần suất cao cho Quỹ & Trader.' },
                { id: 'COCKPIT', name: 'Cockpit Institutional', desc: 'Trợ lý phân tích biểu đồ & dòng tiền tổ chức.' },
                { id: 'OPENAI', name: 'OpenAI / DeepSeek', desc: 'GPT-4o, DeepSeek-V3 qua OpenAI-compatible API.' },
                { id: 'GEMINI', name: 'Google Gemini 2.0', desc: 'Gemini 2.0 Flash tốc độ cực cao, nhận diện cấu trúc.' },
                { id: 'CLAUDE', name: 'Anthropic Claude 3.5', desc: 'Claude 3.5 Sonnet tư duy chiến lược đỉnh cao.' }
              ].map((p) => {
                const isActive = aiHubStatus?.activeProvider === p.id;
                return (
                  <div
                    key={p.id}
                    onClick={() => setSelectedProvider(p.id)}
                    className={`p-4 rounded-xl border transition-all cursor-pointer flex flex-col justify-between space-y-3 ${
                      isActive 
                        ? 'bg-cyan-500/15 border-cyan-400 shadow-[0_0_20px_rgba(0,240,255,0.25)]' 
                        : selectedProvider === p.id
                        ? 'bg-slate-900 border-white/30'
                        : 'bg-slate-950/60 border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between">
                        <h4 className="font-display font-bold text-sm text-white">{p.name}</h4>
                        {isActive && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-cyan-400 text-slate-950 uppercase">
                            ĐANG CHẠY
                          </span>
                        )}
                      </div>
                      <p className="text-xs font-mono text-slate-400 mt-2 leading-relaxed">{p.desc}</p>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSwitchAI(p.id);
                      }}
                      className={`w-full py-2 rounded-lg text-xs font-mono font-bold uppercase transition-all cursor-pointer ${
                        isActive
                          ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/40'
                          : 'bg-slate-800 text-slate-200 hover:bg-cyan-600 hover:text-white'
                      }`}
                    >
                      {isActive ? 'Active Provider' : `Kích hoạt ${p.id}`}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Provider Credentials Form */}
          {selectedProvider !== 'MOCK_SANDBOX' && (
            <div className="glass-card p-5 rounded-2xl border border-white/10 space-y-4">
              <h4 className="font-mono font-bold text-xs text-cyan-300 uppercase">
                Cấu hình API Key & Endpoint cho: <span className="text-white">{selectedProvider}</span>
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
                <div>
                  <label className="text-slate-400 block mb-1">API Key:</label>
                  <input
                    type="password"
                    placeholder="Nhập khóa API bí mật..."
                    value={providerConfig.apiKey}
                    onChange={(e) => setProviderConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                    className="w-full bg-slate-900 border border-white/15 focus:border-cyan-400 rounded-xl px-3 py-2 text-slate-100"
                  />
                </div>

                <div>
                  <label className="text-slate-400 block mb-1">Endpoint / Base URL:</label>
                  <input
                    type="text"
                    placeholder={selectedProvider === 'OPENAI' ? 'https://api.openai.com/v1' : 'https://your-custom-endpoint/v1'}
                    value={providerConfig.endpoint || providerConfig.baseURL}
                    onChange={(e) => setProviderConfig(prev => ({ ...prev, endpoint: e.target.value, baseURL: e.target.value }))}
                    className="w-full bg-slate-900 border border-white/15 focus:border-cyan-400 rounded-xl px-3 py-2 text-slate-100"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => handleSwitchAI(selectedProvider)}
                  className="px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-mono font-bold text-xs uppercase cursor-pointer transition-all shadow-[0_0_15px_rgba(0,240,255,0.4)]"
                >
                  Lưu & Áp dụng ngay
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CREATE USER MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-panel p-6 rounded-2xl border border-fuchsia-500/40 max-w-md w-full space-y-4 animate-scale-up">
            <h3 className="font-display font-bold text-lg text-white uppercase">
              Tạo tài khoản & Cấp License
            </h3>

            <form onSubmit={handleCreateUser} className="space-y-3 text-xs font-mono">
              <div>
                <label className="text-slate-400 block mb-1">Tên đăng nhập:</label>
                <input
                  type="text"
                  required
                  value={newUser.username}
                  onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full bg-slate-900 border border-white/15 rounded-xl px-3 py-2 text-white"
                  placeholder="ví dụ: trader_vip_01"
                />
              </div>

              <div>
                <label className="text-slate-400 block mb-1">Mật khẩu:</label>
                <input
                  type="password"
                  required
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full bg-slate-900 border border-white/15 rounded-xl px-3 py-2 text-white"
                  placeholder="Mật khẩu truy cập"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-400 block mb-1">Thời hạn (ngày):</label>
                  <input
                    type="number"
                    min="1"
                    value={newUser.licenseDays}
                    onChange={(e) => setNewUser(prev => ({ ...prev, licenseDays: e.target.value }))}
                    className="w-full bg-slate-900 border border-white/15 rounded-xl px-3 py-2 text-white"
                  />
                </div>

                <div>
                  <label className="text-slate-400 block mb-1">Hạng thành viên:</label>
                  <select
                    value={newUser.licenseTier}
                    onChange={(e) => setNewUser(prev => ({ ...prev, licenseTier: e.target.value }))}
                    className="w-full bg-slate-900 border border-white/15 rounded-xl px-3 py-2 text-white"
                  >
                    <option value="PRO">PRO</option>
                    <option value="INSTITUTIONAL">INSTITUTIONAL</option>
                    <option value="ENTERPRISE">ENTERPRISE</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 cursor-pointer"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-bold cursor-pointer"
                >
                  Tạo tài khoản
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
